import React, { useState } from 'react';
import { Bell, BellOff, X, RefreshCw, CheckCircle, AlertTriangle, UserX, FileText, Clock } from 'lucide-react';

const initialNotifications = [
  {
    id: 'n1', unread: true, type: 'info',
    title: 'Menu status updated',
    message: "The Burger House menu has been successfully enabled.",
    time: 'Just now', icon: RefreshCw
  },
  {
    id: 'n2', unread: true, type: 'success',
    title: 'New restaurant added',
    message: "Spice Garden has been registered and is pending review.",
    time: '5 min ago', icon: CheckCircle
  },
  {
    id: 'n3', unread: true, type: 'warning',
    title: 'Commission rate changed',
    message: "Pizza Palace's commission was updated from 8% to 12%.",
    time: '20 min ago', icon: AlertTriangle
  },
  {
    id: 'n4', unread: false, type: 'danger',
    title: 'Restaurant deactivated',
    message: "Green Bites has been marked inactive by the admin.",
    time: '1 hr ago', icon: UserX
  },
  {
    id: 'n5', unread: false, type: 'info',
    title: 'Monthly report ready',
    message: "June 2026 restaurant performance report is now available.",
    time: 'Yesterday', icon: FileText
  }
];

const typeStyles = {
  info:    { bg: '#E6F1FB', color: '#185FA5' },
  success: { bg: '#dcfce7', color: '#166534' },
  warning: { bg: '#fef9c3', color: '#854d0e' },
  danger:  { bg: '#fee2e2', color: '#991b1b' }
};

export default function ShowNotification() {
  const [notifications, setNotifications] = useState(initialNotifications);

  const unreadCount = notifications.filter(n => n.unread).length;

  const dismiss = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  };

  return (
    <div style={{ padding: '24px', maxWidth: '520px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', paddingBottom: '12px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Bell size={20} color="#378ADD" />
          <span style={{ fontSize: '16px', fontWeight: '500', color: '#111827' }}>Notifications</span>
          {unreadCount > 0 && (
            <span style={{
              background: '#378ADD', color: '#fff',
              fontSize: '11px', fontWeight: '500',
              padding: '2px 7px', borderRadius: '10px'
            }}>
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={markAllRead}
          style={{ fontSize: '13px', color: '#378ADD', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Mark all as read
        </button>
      </div>

      {/* Notification List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 0', color: '#9ca3af' }}>
            <BellOff size={36} style={{ display: 'block', margin: '0 auto 8px' }} />
            <p style={{ fontSize: '14px' }}>No new notifications</p>
          </div>
        ) : (
          notifications.map(notif => {
            const Icon = notif.icon;
            const style = typeStyles[notif.type];
            return (
              <div
                key={notif.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '14px',
                  backgroundColor: '#ffffff',
                  border: `0.5px solid ${notif.unread ? '#B5D4F4' : '#e5e7eb'}`,
                  borderLeft: notif.unread ? '3px solid #378ADD' : '0.5px solid #e5e7eb',
                  borderRadius: '10px', padding: '14px 16px',
                  position: 'relative', transition: 'background 0.15s'
                }}
              >
                {/* Unread dot */}
                {notif.unread && (
                  <span style={{
                    position: 'absolute', top: '14px', right: '40px',
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: '#378ADD', display: 'inline-block'
                  }} />
                )}

                {/* Icon */}
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: style.bg, color: style.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Icon size={18} />
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827', margin: '0 0 3px' }}>
                    {notif.title}
                  </p>
                  <p style={{
                    fontSize: '13px', color: '#6b7280', margin: '0 0 6px',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                  }}>
                    {notif.message}
                  </p>
                  <span style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={12} /> {notif.time}
                  </span>
                </div>

                {/* Dismiss */}
                <button
                  onClick={() => dismiss(notif.id)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', padding: '2px', borderRadius: '4px', flexShrink: 0
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
          <button style={{
            fontSize: '13px', color: '#378ADD',
            border: '1px solid #B5D4F4', borderRadius: '8px',
            padding: '7px 20px', background: 'none', cursor: 'pointer'
          }}>
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
}